import { useEffect, useState } from "react";
import {
  Home,
  ShoppingBag,
  Tag,
  Users,
  BarChart3,
  Megaphone,
  Percent,
  Store,
  Settings,
  Search,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { CountUp, useInViewRef } from "./shared";
import shopifyLogo from "@/assets/shopify-logo.svg";

const LOOP_MS = 6000;

const NAV = [
  { icon: Home, label: "Home" },
  { icon: ShoppingBag, label: "Orders", badge: "348" },
  { icon: Tag, label: "Products" },
  { icon: Users, label: "Customers" },
  { icon: BarChart3, label: "Analytics", active: true },
  { icon: Megaphone, label: "Marketing" },
  { icon: Percent, label: "Discounts" },
];

// Two ascending lines for "Sales over time"
const CURRENT_PATH =
  "M30,150 L60,138 L90,130 L120,118 L150,108 L180,98 L210,86 L240,74 L270,62 L300,50";
const COMPARE_PATH =
  "M30,165 L60,160 L90,156 L120,150 L150,144 L180,136 L210,128 L240,118 L270,108 L300,100";

export function ShopifyDashboardVisual() {
  const { ref, inView } = useInViewRef<HTMLDivElement>();
  const [cycle, setCycle] = useState(0);

  // Re-trigger the count-ups and chart draw on a loop once in view
  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => setCycle((c) => c + 1), LOOP_MS);
    return () => clearInterval(id);
  }, [inView]);

  const start = inView;
  const animKey = cycle;

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden rounded-xl border border-border/70 bg-white"
    >
      {/* Top bar */}
      <div className="flex items-center gap-3 border-b border-border/60 px-3 py-2 sm:px-4 sm:py-3">
        <img src={shopifyLogo} alt="Shopify" className="h-5 w-auto sm:h-6" />

        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <div className="h-6 rounded-md border border-border/70 bg-background pl-7 pr-2 text-[10px] leading-6 text-muted-foreground">
            Search
          </div>
        </div>
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-[9px] font-bold text-white">
          JM
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-[28%] shrink-0 border-r border-border/60 py-2 text-[10px]">
          <nav className="space-y-0.5 px-1.5">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className={`flex items-center justify-between gap-1.5 rounded-md px-1.5 py-1 ${
                    item.active
                      ? "bg-muted text-foreground font-semibold"
                      : "text-muted-foreground"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <Icon className="h-3 w-3" />
                    <span>{item.label}</span>
                  </div>
                  {item.badge && (
                    <span className="rounded bg-muted px-1 text-[8px] text-muted-foreground">
                      {item.badge}
                    </span>
                  )}
                </div>
              );
            })}
          </nav>
          <div className="mt-3 px-3 text-[8px] uppercase tracking-wide text-muted-foreground/70">
            Sales channels
          </div>
          <div className="mt-1 space-y-0.5 px-1.5 text-muted-foreground">
            <div className="flex items-center justify-between rounded-md px-1.5 py-1">
              <span>Channels</span>
              <ChevronRight className="h-2.5 w-2.5" />
            </div>
            <div className="flex items-center gap-1.5 rounded-md px-1.5 py-1">
              <Store className="h-3 w-3" />
              <span>Online Store</span>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-1.5 px-3 text-muted-foreground">
            <Settings className="h-3 w-3" />
            <span>Settings</span>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1 p-3 sm:p-4">
          <h3 className="text-sm font-semibold text-foreground sm:text-base">
            Overview
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-md border border-border/70 bg-background px-2 py-1 text-[9px] font-medium text-foreground">
              May 1 – Jul 28, 2025
            </div>
            <div className="flex items-center gap-1 rounded-md border border-border/70 bg-background px-2 py-1 text-[9px] text-muted-foreground">
              Compare: Feb 1 – Apr 30, 2025
              <ChevronDown className="h-2.5 w-2.5" />
            </div>
          </div>

          {/* KPI cards */}
          <div className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
            <Kpi label="Sales" target={150} start={start} animKey={animKey} />
            <Kpi
              label="Conversion rate"
              target={22}
              start={start}
              animKey={animKey}
            />
            <Kpi
              label="Avg. order value"
              target={18}
              start={start}
              animKey={animKey}
            />
            <Kpi
              label="Returning"
              target={35}
              start={start}
              animKey={animKey}
            />
          </div>

          {/* Chart */}
          <div className="mt-3 rounded-md border border-border/70 bg-background p-2 sm:p-3">
            <div className="text-[10px] font-semibold text-foreground">
              Sales over time
            </div>
            <svg
              key={animKey}
              viewBox="0 25 320 155"
              className="h-auto w-full"
              aria-hidden
              role="img"
            >
              <defs>
                <linearGradient id="sd-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* gridlines */}
              <g stroke="currentColor" strokeOpacity="0.08">
                <line x1="30" y1="40" x2="310" y2="40" />
                <line x1="30" y1="80" x2="310" y2="80" />
                <line x1="30" y1="120" x2="310" y2="120" />
                <line x1="30" y1="160" x2="310" y2="160" />
              </g>

              {/* y labels */}
              <g
                fill="currentColor"
                fillOpacity="0.55"
                fontSize="8"
                textAnchor="end"
              >
                <text x="26" y="42">$30K</text>
                <text x="26" y="82">$18K</text>
                <text x="26" y="122">$6K</text>
                <text x="26" y="162">$0</text>
              </g>

              {/* x labels */}
              <g
                fill="currentColor"
                fillOpacity="0.55"
                fontSize="8"
                textAnchor="middle"
              >
                <text x="40" y="175">May 1</text>
                <text x="105" y="175">May 29</text>
                <text x="180" y="175">Jun 26</text>
                <text x="255" y="175">Jul 24</text>
              </g>

              {/* area fill (revealed via clipPath progressing) */}
              <clipPath id="sd-reveal">
                <rect x="30" y="0" width="0" height="180">
                  <animate
                    attributeName="width"
                    from="0"
                    to="280"
                    dur="1.6s"
                    fill="freeze"
                  />
                </rect>
              </clipPath>

              <g clipPath="url(#sd-reveal)">
                <path
                  d={`${CURRENT_PATH} L300,160 L30,160 Z`}
                  fill="url(#sd-fill)"
                />
                <path
                  id="sd-current"
                  d={CURRENT_PATH}
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d={COMPARE_PATH}
                  fill="none"
                  stroke="#2563eb"
                  strokeOpacity="0.6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray="3 3"
                />
              </g>

              {/* pulsing dot riding the current line */}
              <circle r="3" fill="#2563eb">
                <animateMotion
                  dur="6s"
                  repeatCount="indefinite"
                  rotate="auto"
                  keyPoints="0;1"
                  keyTimes="0;1"
                  calcMode="linear"
                >
                  <mpath href="#sd-current" />
                </animateMotion>
                <animate
                  attributeName="r"
                  values="2.5;4.5;2.5"
                  dur="1.6s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle r="3" fill="#2563eb" opacity="0.35">
                <animateMotion dur="6s" repeatCount="indefinite">
                  <mpath href="#sd-current" />
                </animateMotion>
                <animate
                  attributeName="r"
                  values="3;10;3"
                  dur="1.6s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.5;0;0.5"
                  dur="1.6s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>

            {/* legend */}
            <div className="mt-1 flex flex-wrap items-center justify-center gap-3 text-[8px] text-muted-foreground">
              <div className="flex items-center gap-1">
                <span className="h-[2px] w-4 rounded bg-[#2563eb]" />
                May 1 – Jul 28, 2025
              </div>
              <div className="flex items-center gap-1">
                <span
                  className="h-[2px] w-4 rounded"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(90deg,#2563eb 0 3px,transparent 3px 6px)",
                  }}
                />
                Feb 1 – Apr 30, 2025
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function Kpi({
  label,
  target,
  start,
  animKey,
}: {
  label: string;
  target: number;
  start: boolean;
  animKey: number;
}) {
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (!start) return;
    setPulse(false);
    const t = setTimeout(() => setPulse(true), 1800);
    const t2 = setTimeout(() => setPulse(false), 2300);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [start, animKey]);

  return (
    <div className="rounded-md border border-border/70 bg-background p-2">
      <div className="text-[8px] text-muted-foreground sm:text-[9px]">
        {label}
      </div>
      <div
        className={`mt-0.5 text-sm font-bold tabular-nums text-emerald-600 transition-transform duration-300 ease-out sm:text-base ${
          pulse ? "scale-110" : "scale-100"
        }`}
        style={{ transformOrigin: "left center" }}
      >
        <span key={animKey}>
          + <CountUp target={target} start={start} suffix="%" />
        </span>
      </div>
    </div>
  );
}
